/*
============================================================
 SampleDataSource - Loads sample puzzle data from local files
------------------------------------------------------------
 Implements the data source interface for sample puzzles
 stored in a dedicated subdirectory (VITE_DATA_PATH/sample/)
 useful for demos, tutorials, and onboarding.
============================================================
*/

/**
 * Data source for loading sample puzzles from a subdirectory
 * @class SampleDataSource
 */
export class SampleDataSource {
  /**
   * @param {string} samplePath - Base path to sample data files (e.g., './data/sample')
   */
  constructor(samplePath) {
    this.samplePath = samplePath;
  }

  /**
   * Fetch sample puzzle data from a local JSON file
   * @async
   * @param {string} sampleId - The sample puzzle identifier (without .json extension)
   * @returns {Promise<Object>} The puzzle data object
   * @throws {Error} If file not found or invalid
   */
  async fetch(sampleId) {
    if (!sampleId) {
      throw new Error("Sample ID is required");
    }

    const trimmedId = sampleId.trim();
    const url = `${this.samplePath}/${trimmedId}.json`;
    console.log(`[SampleDataSource] Fetching from: ${url}`);

    try {
      const response = await fetch(url);

      if (!response.ok) {
        throw new Error(`HTTP ${response.status}: File not found`);
      }

      const data = await response.json();

      // Validate structure
      if (!data || !data.metadata || !data.finmodel) {
        throw new Error("Invalid puzzle data structure");
      }

      // Validate metadata has required fields
      if (!data.metadata.title) {
        throw new Error(
          "Invalid puzzle data structure: metadata.title is required",
        );
      }

      return data;
    } catch (error) {
      console.error(
        `[SampleDataSource] Error loading ${sampleId}:`,
        error.message,
      );
      throw error;
    }
  }
}
